import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {NavService} from '../../_shared/nav/nav.service';
import {SidebarNode, SidebarOptions} from '../../_shared/nav/nav.interfaces';
import {Folder} from '../folder';
import {FolderService} from '../folder.service';
import {FolderListComponent} from '../folder-list/folder-list.component';
import {Project} from '../../project/project';
import {ProjectService} from '../../project/project.service';
import {ProjectListComponent} from '../../project/project-list/project-list.component';
import {MoveModalComponent} from '../move-modal/move-modal.component';

@Component({
    templateUrl: './folders-view.component.html',
    styleUrls: ['./folders-view.component.css']
})
export class FoldersViewComponent implements OnInit, OnDestroy {
    router: Router;

    hasFolderId: boolean;
    folderId: number;
    folder: Folder;
    routeSub: any;
    breadcrumbLinks: string[] = [];

    selectedFolder: Folder;
    selectedProject: Project;

    newFolderName: string;
    waitingServerResponse = false;

    startFolder = {
        iconClass: 'fa-folder',
        label: 'Inicio',
        url: '/carpetas',
        children: []
    };

    startInode = {
        id: null,
        name: 'Inicio',
        children: [],
        parent: undefined
    };

    isUniqueFolder = true;

    private navOptions: SidebarOptions = undefined;

    @ViewChild('folderList') private folderList: FolderListComponent;

    @ViewChild('projectList') private projectList: ProjectListComponent;

    @ViewChild('newFolderBtn') private newFolderModal: ElementRef;
    @ViewChild('newFolderCloseBtn') private newFolderCloseModal: ElementRef;

    @ViewChild('editFolderBtn') private editFolderBtn: ElementRef;
    @ViewChild('editFolderCloseBtn') private editFolderCloseBtn: ElementRef;

    @ViewChild('deleteBtn') private deleteBtn: ElementRef;
    @ViewChild('deleteCloseBtn') private deleteCloseBtn: ElementRef;

    @ViewChild('moveModal') private moveModal: MoveModalComponent;


    private static parseToSidebarNode(root): SidebarNode {
        const ret = {
            iconClass: 'fa-folder-o',
            label: root['name'],
            url: ['/carpetas', root['id']],
            children: []
        };

        if (root['children']) {
            for (const child of root['children']) {
                ret.children.push(FoldersViewComponent.parseToSidebarNode(child));
            }
        }

        return ret;
    }

    constructor(private route: ActivatedRoute,
                private folderService: FolderService,
                private projectService: ProjectService,
                private nav: NavService,
                _router: Router) {
        this.router = _router;
        this.navOptions = {
            title: 'CARPETAS',
            entries: [this.startFolder],
            nodeClass: ''
        };
    }

    ngOnInit() {
        this.updateNavOptions();

        this.routeSub = this.route.params
            .subscribe((params: Params) => {
                this.folderId = +params['id'];
                this.hasFolderId = !Number.isNaN(this.folderId);
                if (this.hasFolderId) {
                    this.loadFolderData();
                }
            });
    }

    ngOnDestroy(): void {
        this.routeSub.unsubscribe();
    }

    updateNavOptions() {
        this.nav.updateSidebarOptions(this.navOptions);
        this.folderService.getFoldersTree().subscribe(folders => {
            this.startInode.children = folders;
            this.addParentRef(this.startInode, undefined);
            this.startFolder.children = [];
            for (const folder of folders) {
                this.startFolder.children.push(FoldersViewComponent.parseToSidebarNode(folder));
            }
            this.navOptions.entries = [this.startFolder];
            this.nav.updateSidebarOptions(this.navOptions);
        });
    }

    addParentRef(inode, parent) {
        inode.parent = parent;
        for (const child of inode.children) {
            this.addParentRef(child, inode);
        }
        return inode;
    }

    loadFolderData() {
        this.folderService.get(this.folderId).subscribe(folder => {
            this.folder = folder;
        });
        this.folderService.getTree(this.folderId).subscribe(data => this.breadcrumbLinks = data);
    }

    createFolder() {
        this.waitingServerResponse = true;
        const folderName = this.newFolderName.trim();

        if (!folderName) {
            this.waitingServerResponse = false;
            return;
        }
        const initialFolder: Folder = {name: folderName, id: -1, parent: null, owner: 'invitate'};
        if (this.hasFolderId) {
            initialFolder['parent'] = this.folderId;
        }
        this.folderService.create(initialFolder).subscribe(folder => {
            this.updateNavOptions();
            this.folderList.addFolder(folder);
            this.waitingServerResponse = false;
            this.newFolderCloseModal.nativeElement.click();
            this.newFolderName = '';
        });
    }

    deleteInode() {
        if (this.selectedFolder) {
            this.deleteFolder();
        }
        if (this.selectedProject) {
            this.deleteProject();
        }
    }

    deleteFolder() {
        this.waitingServerResponse = true;
        this.folderService.delete(this.selectedFolder.id).subscribe(
            () => {
                this.updateNavOptions();
                this.folderList.delete(this.selectedFolder);
                this.waitingServerResponse = false;
                this.deleteCloseBtn.nativeElement.click();
                this.selectedFolder = undefined;
            });
    }

    deleteProject() {
        this.waitingServerResponse = true;

        this.projectService.delete(this.selectedProject.id).subscribe(
            () => {
                this.projectList.delete(this.selectedProject);
                this.waitingServerResponse = false;
                this.deleteCloseBtn.nativeElement.click();
                this.selectedProject = undefined;
            });
    }

    editFolder() {
        this.waitingServerResponse = true;

        const newFolder = new Folder();
        newFolder.name = this.newFolderName.trim();
        newFolder.id = this.selectedFolder.id;
        newFolder.parent = this.selectedFolder.parent;

        if (!newFolder.name) {
            this.waitingServerResponse = false;
            return;
        }

        this.folderService.update(newFolder).subscribe(() => {
            this.updateNavOptions();
            this.selectedFolder.name = this.newFolderName;
            this.waitingServerResponse = false;
            this.editFolderCloseBtn.nativeElement.click();
            this.newFolderName = '';
        });
    }

    move(newParentId: number) {
        if (this.selectedFolder) {
            this.moveFolder(newParentId);
        }
        if (this.selectedProject) {
            this.moveProject(newParentId);
        }
    }

    moveFolder(newParentId) {
        if (newParentId === this.selectedFolder.parent || newParentId === this.selectedFolder.id) {
            this.waitingServerResponse = false;
            this.moveModal.close();
            return;
        }
        this.waitingServerResponse = true;

        const newFolder = new Folder();
        newFolder.name = this.selectedFolder.name;
        newFolder.id = this.selectedFolder.id;
        newFolder.parent = newParentId;

        this.folderService.update(newFolder).subscribe(folder => {
            this.updateNavOptions();
            this.folderList.delete(folder);
            this.waitingServerResponse = false;
            this.moveModal.close();
        }, () => {
            this.waitingServerResponse = false;
        });
    }

    moveProject(newParentId) {
        if (newParentId === this.selectedProject.parent_inode) {
            this.waitingServerResponse = false;
            this.moveModal.close();
            return;
        }

        this.waitingServerResponse = true;

        const newProject = Project.copy(this.selectedProject);
        newProject.parent_inode = newParentId;
        newProject.warehouse_type = undefined;

        this.projectService.update(newProject).subscribe(project => {
            this.projectList.delete(project);
            this.waitingServerResponse = false;
            this.moveModal.close();
        }, () => {
            this.waitingServerResponse = false;
        });
    }

    eventHandler(event) {
        switch (event.type) {
            case 'folder-create':
                this.selectedFolder = undefined;
                this.selectedProject = undefined;
                this.newFolderModal.nativeElement.click();
                this.checkUniqueFolder(this.newFolderName);
                break;
            case 'folder-delete':
                this.selectedFolder = event.folder;
                this.selectedProject = undefined;
                this.deleteBtn.nativeElement.click();
                break;
            case 'folder-edit':
                this.selectedFolder = event.folder;
                this.selectedProject = undefined;
                this.newFolderName = event.folder.name;
                this.editFolderBtn.nativeElement.click();
                break;
            case 'folder-move':
                this.selectedFolder = event.folder;
                this.selectedProject = undefined;
                this.moveModal.open();
                break;
            case 'project-delete':
                this.selectedFolder = undefined;
                this.selectedProject = event.project;
                this.deleteBtn.nativeElement.click();
                break;
            case 'project-move':
                this.selectedFolder = undefined;
                this.selectedProject = event.project;
                this.moveModal.open();
                break;
        }
    }

    checkUniqueFolder(name){
      this.isUniqueFolder =  true;
      if(name) for(let i = 0; i < this.folderList.folders.length; i++){
        if( (!this.selectedFolder || (this.selectedFolder &&
            this.folderList.folders[i].id !== this.selectedFolder.id)) &&
            this.folderList.folders[i].name === name.trim()){
          this.isUniqueFolder = false;
          break;
        }
      }
    }
}



// WEBPACK FOOTER //
// ./src/app/folder/folders-view/folders-view.component.ts